import { useMutation } from '@apollo/client';
import { faChevronUp, faPauseCircle } from '@fortawesome/pro-regular-svg-icons';
import { Button, Divider, LinkButton } from '@indigo-ag/component-module';
import {
  AgFinancingInput,
  AgFinancingPhase2Status,
  Phase2 as IPhase2,
  OpportunityType,
  Phase2Input
} from '@indigo-ag/schema';
import { ExecutionResult } from 'graphql';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { UserContext } from 'core/components/GateWay/GateWay';
import i18next from 'core/i18n';
import { FieldMonitoringSection } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/FieldMonitoringSection/FieldMonitoringSection';
import { NotificationSection } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/NotificationSection/NotificationSection';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { Permissions, Phase2Util } from 'shared';
import { CollateralSection } from '../../components/CollateralSection/CollateralSection';
import { CreditSection } from '../../components/CreditSection/CreditSection';
import { DecisionModal } from '../../shared/DecisionModal/DecisionModal';
import { OrderDetail } from '../../components/OrderDetail/OrderDetail';
import { PhaseWrapper } from '../../shared/PhaseWrapper';
import { BackgroundCheckType } from '../../shared/background-check/components/BackgroundCheckType/BackgroundCheckType';
import { DecisionSheet } from './DecisionSheet';
import { ApprovePhase2 } from './Phase2Actions/ApprovePhase2/ApprovePhase2';
import { DenyPhase2 } from './Phase2Actions/DenyPhase2/DenyPhase2';
import { EscalatePhase2 } from './Phase2Actions/EscalatePhase2/EscalatePhase2';
import { Phase2State } from './Phase2State/Phase2State';

const StyledDivider = styled(Divider)`
  margin-top: 16px;
  color: #d7e4e9;
`;
interface IPhase2Context {
  updatePhase: (newPhase2Info: Partial<Phase2Input>) => Promise<ExecutionResult<any>>;
}

export const Phase2Context = React.createContext<IPhase2Context>({
  updatePhase: () => Promise.resolve({})
});

interface Phase2Props {
  phaseInfo?: IPhase2;
}

export const Phase2: React.FunctionComponent<Phase2Props> = ({ phaseInfo }) => {
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  /**
   * Context
   */
  const { agFinancingId, orderInfo, agFinance, creditApprovalDataQuery, canStartPhase2 } = useContext(
    FinanceProcessContext
  );
  const { hasPermission } = useContext(UserContext);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  const hasPromissoryNote = Phase2Util.hasPromissoryNote(agFinance);
  const updatePhase = (newPhase2Info: Partial<Phase2Input>) => {
    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinancingId,
      phase2: {
        ...newPhase2Info
      }
    };

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    });
  };

  const onHoldPhase2 = (decisionNotes: string) => {
    return updatePhase({ status: AgFinancingPhase2Status.ON_HOLD, decisionNotes });
  };

  const showAnalysis =
    phaseInfo?.status === AgFinancingPhase2Status.DENIED ||
    phaseInfo?.status === AgFinancingPhase2Status.ON_HOLD ||
    phaseInfo?.status === AgFinancingPhase2Status.WAITING_COMMITTEE_DECISION;

  const isAwaitingCommercialDeal = phaseInfo?.status === AgFinancingPhase2Status.AWAITING_COMMERCIAL_DEAL;
  const hasCommercialApproval = Phase2Util.getCommercialApproval(orderInfo);
  const canViewDecisionSheet = hasPermission(Permissions.FA_INTERNAL) || hasPermission(Permissions.FA_ADMIN);

  const showGeoreferencing =
    agFinance?.opportunityType !== OpportunityType.SUPPLIER &&
    agFinance?.opportunityType !== OpportunityType.BARTER_LIMIT;

  const showCollateralAnalysis =
    agFinance?.opportunityType !== OpportunityType.SUPPLIER &&
    agFinance?.opportunityType !== OpportunityType.BARTER_LIMIT;

  const title =
    agFinance?.opportunityType === OpportunityType.BARTER_GUARANTEE
      ? i18next.t('collateralAnalysis')
      : i18next.t('creditAnalysis');

  return (
    <Phase2Context.Provider
      value={{
        updatePhase
      }}
    >
      <div id="topOfPage"></div>
      <PhaseWrapper
        title={title}
        subtitleElement={<BackgroundCheckType orderInfo={orderInfo} />}
        actionElement={
          <>
            <EscalatePhase2 />
            <DenyPhase2 />
            <ApprovePhase2 />
            {Phase2Util.canOnHold(phaseInfo) ? (
              <>
                <Button
                  variant="secondary"
                  size="small"
                  // all opp in credit approval
                  disabled={!canStartPhase2}
                  faIconAfter={faPauseCircle}
                  className="text-transform-capitalize"
                  onClick={() => setIsDecisionOpen(true)}
                >
                  {i18next.t('onHold')}
                </Button>
                <DecisionModal
                  title={`${i18next.t('onHold')} ${i18next.t('phase')} 2`}
                  message={i18next.t('phase2OnHoldDisclaimer')}
                  onConfirmAction={onHoldPhase2}
                  open={isDecisionOpen}
                  closeModal={() => setIsDecisionOpen(false)}
                />
              </>
            ) : (
              undefined
            )}
          </>
        }
      >
        {showAnalysis && <Phase2State phaseInfo={phaseInfo} />}
        {isAwaitingCommercialDeal && !hasCommercialApproval && (
          <NotificationSection
            title={i18next.t('awaitingCommercialDeal')}
            subtitle={i18next.t('awaitingCommercialDealMessage')}
            status="warning"
          />
        )}
        <div id="creditAnalysis"></div>
        {agFinance?.opportunityType !== OpportunityType.BARTER_GUARANTEE && (
          <CreditSection creditInfo={phaseInfo?.credit} title={title} />
        )}
        {canViewDecisionSheet && (
          <>
            <div id="decisionSheet"></div>
            <StyledDivider />
            <DecisionSheet
              loading={creditApprovalDataQuery?.loading}
              creditApprovalData={creditApprovalDataQuery?.data?.creditApprovalData}
            />
          </>
        )}
        {!hasPromissoryNote && showGeoreferencing && (
          <>
            <div id="georeferencing"></div>
            <FieldMonitoringSection fieldMonitoringInfo={phaseInfo?.fieldMonitoring} />
          </>
        )}
        <div id="collateralAnalysis"></div>
        {showCollateralAnalysis && (
          <CollateralSection
            collateralInfo={phaseInfo?.collateral}
            matriculas={phaseInfo?.matriculas}
            fieldMonitoringInfo={phaseInfo?.fieldMonitoring}
          />
        )}
        <OrderDetail orderInfo={orderInfo} isOrderDetailsCard />
        <LinkButton to="#topOfPage" faIcon={faChevronUp}>
          {i18next.t('backToTop')}
        </LinkButton>
      </PhaseWrapper>
    </Phase2Context.Provider>
  );
};
